<template>
    <div class="flex flex-col gap-0.5 max-w-120">
        <div class="flex flex-row gap-1.5 items-center text-xs">
            <div v-if="props.companyGroup === 'Koelnmesse'" class="bg-koelnmesse text-gray-50 px-1.75 py-1 rounded-xs">
                <KoelnmesseLogo class="h-4" />
            </div>
            <div
                v-else-if="props.companyGroup === 'Koelncongress'"
                class="bg-koelnmesse text-gray-50 px-1.5 py-1.5 rounded-xs"
            >
                <KoelncongressLogo class="h-3" />
            </div>
            <div v-else-if="props.companyGroup" class="bg-gray-600 px-1.75 py-1 text-gray-50 rounded-xs text-left">
                {{ props.companyGroup }}
            </div>
            <div v-else class="bg-gray-300 rounded-xs py-1 px-1.75 text-gray-600 font-light text-left">
                {{ $t('allocation-tooltip-no-company-group') }}
            </div>

            <div v-if="props.businessDomain" class="text-gray-600 font-light leading-tight text-left">
                {{ props.businessDomain }}
            </div>
            <div v-else class="text-gray-500 font-light leading-tight text-left">
                {{ $t('allocation-tooltip-no-business-domain') }}
            </div>
        </div>

        <div class="flex flex-row gap-1.5 items-start text-2xl leading-[1.15]">
            <div class="font-semibold text-left hyphens-auto text-gray-700">
                {{ props.title }}
            </div>
            <!--
                Exactly one title line box tall, so the badge centres on the title's *first* line however far
                it wraps. No `align-items` value expresses that: `center` drifts to the middle of a wrapped
                title and `baseline` hangs the pill below it. `1lh` resolves against the line-height this
                wrapper inherits from the row, which is why the title's type is set there rather than on it.
            -->
            <div class="flex items-center h-lh">
                <Badge :variant="props.status.variant" size="Small">
                    {{ props.status.label }}
                </Badge>
            </div>
        </div>

        <div class="flex flex-row gap-1.5 items-center">
            <MultipleStopIcon class="size-3.5 text-gray-500 shrink-0" />
            <div class="flex flex-row items-center gap-0.75 text-xs">
                <AllocationVariantIndex
                    :index="props.allocationVariant.index"
                    text-size="Inherit"
                    :is-tooltip-disabled="true"
                />
                <span class="font-normal text-gray-600 text-left leading-[1.15]">
                    ({{ props.allocationVariant.name }})
                </span>
            </div>
            <Badge :variant="props.allocationVariant.status.variant" size="Extra-Small">
                {{ props.allocationVariant.status.label }}
            </Badge>
        </div>

        <div class="flex flex-row gap-1.5 items-center">
            <UserIcon class="size-3.5 text-gray-500 shrink-0" />
            <div v-if="props.responsibles.length > 0" class="flex flex-row gap-1">
                <template v-for="(responsible, index) in props.responsibles" :key="index">
                    <span v-if="index > 0" class="font-light text-gray-400 leading-[1.15] text-xs">&amp;</span>
                    <span class="font-medium text-gray-600 leading-[1.15] text-xs text-left">{{ responsible }}</span>
                </template>
            </div>
            <div v-else class="font-light text-gray-500 text-xs text-left leading-[1.15]">
                {{ $t('allocation-tooltip-no-responsible') }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import AllocationVariantIndex from '../../AllocationVariantIndex/AllocationVariantIndex.vue';
import Badge, { BadgeVariant } from '../../Badge/Badge.vue';
import KoelncongressLogo from '../../Icon/KoelncongressLogo.vue';
import KoelnmesseLogo from '../../Icon/KoelnmesseLogo.vue';
import MultipleStopIcon from '../../Icon/MultipleStopIcon.vue';
import UserIcon from '../../Icon/UserIcon.vue';

export type AllocationTooltipProps = {
    companyGroup: 'Koelnmesse' | 'Koelncongress' | string | null;
    businessDomain: string | null;
    title: string;
    status: {
        variant: BadgeVariant;
        label: string;
    };
    allocationVariant: {
        index: number;
        name: string;
        status: {
            variant: BadgeVariant;
            label: string;
        };
    };
    responsibles: string[];
};

const props = defineProps<AllocationTooltipProps>();
</script>
